import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { UserPlus, Info, WarningCircle, CheckCircle } from "@phosphor-icons/react";
import LoaderLoop from "../shared/loader/LoaderLoop";
import ModalCloseButton from "../ui/ModalCloseButton";
import PhoneInput from "../ui/PhoneInput";
import {
  selectClinicId,
  selectClinicProfile,
  selectTherapistProfile,
  setClinicProfile,
} from "../../store/slices/authSlice";
import { useCreateTeamMemberMutation } from "../../store/services/therapistApi";
import usePlanPermissions from "../../hooks/usePlanPermissions";
import { useToast } from "../../contexts/ToastContext";
import { PERMISSIONS_BY_KEY, getRolePreset } from "../../config/permissionsRegistry";
import { ROLE_OPTIONS, validateMemberForm } from "./teamUtils";

const EMPTY = { firstName: "", lastName: "", email: "", phone: "", clinicRole: "therapist", sendInviteSms: true, inviteNote: "" };
const INVITE_NOTE_MAX = 120;

/** Preview: which notable permissions the preset grants (short list). */
const PREVIEW_KEYS = [
  "patients.viewAllClinic",
  "calendar.viewClinic",
  "calendar.manageOthersAppointments",
  "clinical.viewSensitive",
  "treatments.write",
  "allowFinanceAccess",
  "allowExportData",
];

/**
 * "הוספת איש/אשת צוות" — role-aware version of the old add-therapist modal.
 * Phase 2 adds the SMS invite checkbox + note here.
 */
function AddTeamMemberModal({ onClose, onCreated }) {
  const dispatch = useDispatch();
  const clinicId = useSelector(selectClinicId);
  const clinicProfile = useSelector(selectClinicProfile);
  const me = useSelector(selectTherapistProfile);
  const actorIsOwner = me?.role === "owner";
  const { checkTherapist, PlanLimitModalComponent } = usePlanPermissions();
  const [createTeamMember] = useCreateTeamMemberMutation();
  const { showError: toastError } = useToast();

  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null); // null | "submitting" | "success" | "error"
  const [message, setMessage] = useState("");

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const preview = useMemo(() => {
    if (form.clinicRole === "manager") return ["גישת מנהל/ת מלאה לכל חלקי המערכת"];
    const preset = getRolePreset(form.clinicRole, clinicProfile);
    return PREVIEW_KEYS.filter((k) => preset[k]).map((k) => PERMISSIONS_BY_KEY[k].label);
  }, [form.clinicRole, clinicProfile]);

  const handleSubmit = async () => {
    const v = validateMemberForm(form);
    if (Object.keys(v).length > 0) {
      setErrors(v);
      return;
    }
    const canProceed = await checkTherapist();
    if (!canProceed) return;

    setStatus("submitting");
    setMessage("");
    try {
      const res = await createTeamMember({
        clinicId,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone,
        clinicRole: form.clinicRole,
        sendInviteSms: form.sendInviteSms,
        inviteNote: form.inviteNote.trim(),
      }).unwrap();
      if (clinicProfile?.isSoloClinic) {
        dispatch(setClinicProfile({ ...clinicProfile, isSoloClinic: false }));
      }
      setStatus("success");
      if (form.sendInviteSms && res?.inviteSmsSent !== true) {
        setMessage("איש/אשת הצוות נוסף/ה בהצלחה, אך ה-SMS לא נשלח. ניתן לשלוח שוב מכרטיס איש הצוות.");
        toastError("המטפל/ת נוסף/ה אך ה-SMS לא נשלח. ניתן לשלוח שוב.");
      } else {
        setMessage(form.sendInviteSms ? "איש/אשת הצוות נוסף/ה ונשלח/ה הודעת SMS עם קישור לכניסה!" : "איש/אשת הצוות נוסף/ה בהצלחה!");
      }
      onCreated?.({ upgradedToMulti: res?.upgradedToMulti === true });
      setTimeout(onClose, 1600);
    } catch (err) {
      console.error("[CK-5543] Error creating team member:", {
        errorCode: "CK-5543",
        errorMessage: err?.message,
        functionCode: err?.code,
        clinicId,
        clinicRole: form.clinicRole,
        timestamp: new Date().toISOString(),
      });
      let text = "שגיאה ביצירת איש/אשת הצוות. נסו שוב.";
      if (err?.code === "functions/already-exists") text = err.message;
      else if (err?.code === "functions/permission-denied") text = err.message || "אין לך הרשאה להוסיף אנשי צוות.";
      else if (err?.message) text = err.message;
      setStatus("error");
      setMessage(text);
    }
  };

  const submitting = status === "submitting";

  return (
    <div className="tm-overlay" onClick={submitting ? undefined : onClose}>
      <PlanLimitModalComponent />
      <div className="tm-modal narrow" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="tm-modal-header">
          <div className="tm-modal-title">
            <span className="tm-modal-title-icon">
              <UserPlus size={20} weight="fill" />
            </span>
            <div className="tm-modal-title-text">
              <span className="tm-eyebrow">צוות הקליניקה</span>
              <h3>הוספת איש/אשת צוות</h3>
            </div>
          </div>
          <div className="tm-modal-header-actions">
            <ModalCloseButton onClick={onClose} />
          </div>
        </div>

        {status === "success" ? (
          <div className="tm-success">
            <CheckCircle size={44} weight="fill" />
            <p>{message}</p>
          </div>
        ) : (
          <>
            <div className="tm-body">
              <div className="tm-field">
                <span className="tm-label">תפקיד</span>
                <div className="tm-seg" role="radiogroup" aria-label="תפקיד">
                  {ROLE_OPTIONS.map((opt) => {
                    const locked = opt.key === "manager" && !actorIsOwner;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        role="radio"
                        aria-checked={form.clinicRole === opt.key}
                        className={`tm-seg-opt ${form.clinicRole === opt.key ? "active" : ""}`}
                        disabled={locked}
                        title={locked ? "רק בעל/ת הקליניקה יכול/ה למנות מנהלים" : undefined}
                        onClick={() => setField("clinicRole", opt.key)}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <span className="tm-caption">
                  יקבל/תקבל: {preview.length ? preview.join(" · ") : "הרשאות בסיסיות בלבד"}. ניתן לשנות בכל עת.
                </span>
              </div>

              <div className="tm-row-2">
                <div className="tm-field">
                  <label className="tm-label required">שם פרטי</label>
                  <input
                    className={`tm-input ${errors.firstName ? "error" : ""}`}
                    value={form.firstName}
                    onChange={(e) => setField("firstName", e.target.value)}
                    placeholder="שם פרטי"
                  />
                  {errors.firstName && <span className="tm-error">{errors.firstName}</span>}
                </div>
                <div className="tm-field">
                  <label className="tm-label required">שם משפחה</label>
                  <input
                    className={`tm-input ${errors.lastName ? "error" : ""}`}
                    value={form.lastName}
                    onChange={(e) => setField("lastName", e.target.value)}
                    placeholder="שם משפחה"
                  />
                  {errors.lastName && <span className="tm-error">{errors.lastName}</span>}
                </div>
              </div>

              <div className="tm-field">
                <label className="tm-label required">אימייל</label>
                <input
                  type="email"
                  dir="ltr"
                  className={`tm-input ${errors.email ? "error" : ""}`}
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  placeholder="email@example.com"
                />
                {errors.email && <span className="tm-error">{errors.email}</span>}
              </div>

              <div className="tm-field">
                <label className="tm-label required">טלפון נייד</label>
                <PhoneInput value={form.phone} onChange={(v) => setField("phone", v)} error={errors.phone} />
              </div>

              <label className="tm-check">
                <input
                  type="checkbox"
                  checked={form.sendInviteSms}
                  onChange={(e) => setField("sendInviteSms", e.target.checked)}
                />
                <span>שלח/י הודעת SMS עם קישור לכניסה</span>
              </label>
              {form.sendInviteSms && (
                <div className="tm-field">
                  <label className="tm-label">הערה אישית ב-SMS (אופציונלי)</label>
                  <input
                    className="tm-input"
                    maxLength={INVITE_NOTE_MAX}
                    value={form.inviteNote}
                    onChange={(e) => setField("inviteNote", e.target.value)}
                    placeholder="לדוגמה: נתראה ביום ראשון בבוקר!"
                  />
                  <span className="tm-caption">{form.inviteNote.length}/{INVITE_NOTE_MAX} · ההודעה נשלחת על חשבון CliniKit ולא נספרת במכסת ה-SMS של הקליניקה.</span>
                </div>
              )}

              <div className="tm-note">
                <Info size={16} weight="fill" />
                <span>הכניסה למערכת היא עם מספר הטלפון שיוזן (קוד SMS). מקום בתוכנית נספר לכל תפקיד, כולל מזכירות.</span>
              </div>

              {status === "error" && (
                <div className="tm-banner-error">
                  <WarningCircle size={18} weight="fill" />
                  <span>{message}</span>
                </div>
              )}
            </div>

            <div className="tm-footer">
              <button type="button" className="tm-btn secondary" onClick={onClose} disabled={submitting}>
                ביטול
              </button>
              <button type="button" className="tm-btn primary" onClick={handleSubmit} disabled={submitting}>
                {submitting ? <LoaderLoop size="xs" /> : <UserPlus size={16} weight="bold" />}
                {submitting ? "יוצר משתמש..." : "הוספה לצוות"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AddTeamMemberModal;
